import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
  input,
  output,
} from "@angular/core";
import { AppStateStore } from "../core/sim/app-state.store";
import { AudioService } from "../core/audio/audio.service";
import { BoatControlsStore } from "../core/sim/boat-controls.store";
import { BoatTelemetryStore } from "../core/sim/boat-telemetry.store";
import { ImpactScoreStore } from "../core/sim/impact-score.store";
import { SimService } from "../core/sim/sim.service";
import { AnchorMenuStore } from "../core/sim/anchor-menu.store";
import type { AppState } from "../../simulation-core";

/**
 * Hosts the WebGPU canvas and triggers `SimService.attach` once the DOM
 * element exists. Emits `(ready)` after the sim handle resolves so the
 * shell can swap placeholders for the real controls via @defer.
 */
@Component({
  selector: "po-scene-canvas",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./scene-canvas.component.html",
  styleUrl: "./scene-canvas.component.css",
})
export class SceneCanvasComponent implements AfterViewInit, OnDestroy {
  private readonly sim = inject(SimService);
  private readonly stateStore = inject(AppStateStore);
  private readonly controls = inject(BoatControlsStore);
  private readonly telemetry = inject(BoatTelemetryStore);
  private readonly audio = inject(AudioService);
  private readonly impactScore = inject(ImpactScoreStore);
  private readonly anchorMenu = inject(AnchorMenuStore);
  private readonly zone = inject(NgZone);
  private telemetryRaf = 0;

  readonly initialState = input<Partial<AppState> | null>(null);
  readonly backgroundPreview = input(false);
  readonly ready = output<void>();

  @HostBinding("class.preview")
  protected get previewClass(): boolean {
    return this.backgroundPreview();
  }

  @ViewChild("canvas", { static: true })
  private readonly canvasRef!: ElementRef<HTMLCanvasElement>;

  async ngAfterViewInit(): Promise<void> {
    const initialState = this.initialState();
    await this.sim.attach(this.canvasRef.nativeElement, {
      ...(initialState ? { initialState } : {}),
      backgroundPreview: this.backgroundPreview(),
    });
    this.stateStore.syncFromHandle();
    if (!this.audio.muted()) {
      void this.audio.init();
    }
    this.startTelemetryPoll();
    this.installAnchorClickHandler();
    this.ready.emit();
  }

  ngOnDestroy(): void {
    if (this.telemetryRaf) cancelAnimationFrame(this.telemetryRaf);
    this.sim.detach();
  }

  /**
   * Polls live boat throttle/steer each frame and pushes them into
   * BoatControlsStore so the wheel/lever widgets follow keyboard input.
   * Runs OUTSIDE Angular zone — the store uses signals, OnPush views
   * pick up changes via the signal graph.
   */
  private startTelemetryPoll(): void {
    this.zone.runOutsideAngular(() => {
      const tick = () => {
        const t = this.sim.boatTelemetry();
        if (t) {
          this.controls.syncFromSim(
            t.throttle,
            t.steer,
            Math.max(-1, Math.min(1, t.engineAngle / ((45 * Math.PI) / 180))),
            t.helmWheelLockDeg,
            t.steering,
            t.engineType,
            t.engineSteeringCoupled,
            t.gas,
            t.gear,
          );
          this.telemetry.update(t);
        }
        const impacts = this.sim.consumeImpactEvents();
        if (impacts.length > 0) {
          this.audio.handleImpacts(impacts);
          // Accumulates penalty points; no UI yet, future scorecard
          // will read `ImpactScoreStore.totalPoints()` and friends.
          this.impactScore.ingest(impacts);
        }
        this.telemetryRaf = requestAnimationFrame(tick);
      };
      this.telemetryRaf = requestAnimationFrame(tick);
    });
  }

  /**
   * Listen for clicks on the WebGPU canvas. The browser fires `click`
   * only after a pointerdown+up that didn't move much, so it cleanly
   * distinguishes taps from camera drags (which the OrbitalCameraController
   * handles via pointermove). On a tap, convert canvas-relative pixel
   * coords to NDC (-1..+1, +Y up) and ask the picker which mooring
   * anchor was hit. If something hit, open the contextual menu.
   */
  private installAnchorClickHandler(): void {
    const canvas = this.canvasRef.nativeElement;
    if (this.backgroundPreview()) return;
    canvas.addEventListener("click", (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const ndcX = (px / rect.width) * 2 - 1;
      const ndcY = 1 - (py / rect.height) * 2;
      const pick = this.sim.pickAnchorAtNdc(ndcX, ndcY);
      if (!pick) {
        this.anchorMenu.close();
        return;
      }
      this.anchorMenu.open({
        pick,
        screenX: e.clientX,
        screenY: e.clientY,
      });
    });
  }
}
